import { Link, useParams } from "react-router-dom";
import { useSeo } from "../../lib/useSeo";

import { Btn } from "../../lib/Btn";
import { Card } from "../../lib/Card";
import { Icon, type IconName } from "../../lib/Icon";
import { Pill } from "../../lib/Pill";
import { Slats } from "../../lib/Slats";

type VerticalKey = "multifamily" | "storage" | "parking" | "rv_boat";

type VerticalSection = {
  key: VerticalKey;
  name: string;
  accentVar: string;
  tagline: string;
  intro: string;
  unitNoun: string;
  marketSize: string;
  capabilities: Array<{ icon: IconName; title: string; body: string }>;
  outcomes: string[];
};

const VERTICAL_SECTIONS: VerticalSection[] = [
  {
    key: "multifamily",
    name: "Multifamily",
    accentVar: "var(--multifamily-accent, #3F6B4B)",
    tagline: "Apartments, condos, senior living — one operator, every door.",
    marketSize: "~50M rental units · the largest property segment in the US",
    intro:
      "Run a 12-unit walkup or a 400-door garden community with the same workflow. Applications, screening, leasing pipeline, resident portal, maintenance Kanban, renewals — built around the landlord-tenant act for your state, not a generic ticketing tool.",
    unitNoun: "doors",
    capabilities: [
      {
        icon: "doc",
        title: "Applications + screening",
        body: "Public apply page per property. Income, ID, and reference checks; auto-decision rules you control. Decline reasons logged for Fair Housing.",
      },
      {
        icon: "chart",
        title: "Leasing pipeline (Kanban)",
        body: "Lead → tour → application → approved → leased. Per-property funnel metrics and aging by stage.",
      },
      {
        icon: "grid",
        title: "Resident portal",
        body: "Pay rent, submit maintenance requests with photos, renew the lease, request move-out — all from the resident portal in their browser.",
      },
      {
        icon: "wallet",
        title: "Rent + utility billing",
        body: "Recurring rent on the 1st, RUBS for utilities, deposit accounting per state, late fees that respect your state's cap.",
      },
      {
        icon: "lock",
        title: "Building access",
        body: "Fob / PIN / mobile-key partners across major access vendors. Code provisions on move-in, revokes only on operator action.",
      },
      {
        icon: "doc",
        title: "State-compliant leases",
        body: "Landlord-tenant act per state — security-deposit cap, notice windows, fair-housing disclosures — wired into the lease engine.",
      },
    ],
    outcomes: [
      "Drop unit turn time from weeks to days",
      "Hit 96%+ leased without an outsourced leasing agent",
      "Clean per-property P&L for every LLC at tax time",
    ],
  },
  {
    key: "storage",
    name: "Self-storage",
    accentVar: "var(--storage-accent, #B5543B)",
    tagline: "Operators of every size — same gate vendor, lower stack cost.",
    marketSize: "~50,000 facilities · $40B+ annual revenue",
    intro:
      "Run online rental, billing, gate codes, lien sales, and accounting from one place. Built for operators of every size — from a single facility to a nationwide portfolio — without the storEDGE/SiteLink enterprise sales motion or the per-transaction cut on tenant rent.",
    unitNoun: "units",
    capabilities: [
      {
        icon: "grid",
        title: "Online rental + e-sign",
        body: "Per-facility public rental page. Pick a unit, sign the lease, pay, get a gate code — all from a phone in under five minutes.",
      },
      {
        icon: "gate",
        title: "7-vendor gate integration",
        body: "PTI Falcon, SecurGuard, INSOMNIAC CIA, Noke, DaVinciLock, OpenTech IntelliGate, Janus — out of the box. Code provisioning + real-time event log.",
      },
      {
        icon: "doc",
        title: "State-compliant lien sales",
        body: "Initiation → notice → publication → auction or cure. Per-state deadlines stamped at notice-send. Certified mail via Lob; auto-cure on payment.",
      },
      {
        icon: "wallet",
        title: "Dunning + late fees per state",
        body: "Automated email + SMS reminder schedules. Late fees enforced per state caps. No accidental over-charging that fails a court challenge.",
      },
      {
        icon: "chart",
        title: "Pricing AI (advisory)",
        body: "Weekly per-unit-size rate recommendations with an AI-written rationale. Approve one click or set an auto-apply policy with a max-delta cap.",
      },
      {
        icon: "lock",
        title: "Stripe Identity for move-ins",
        body: "Optional ID gate at move-in — one verification covers every move-in across your facilities. Cuts your fraud risk without adding friction.",
      },
    ],
    outcomes: [
      "Replace storEDGE + payment processor + gate portal with one stack",
      "Keep 100% of tenant rent — no per-transaction processing margin",
      "Trim ~$850/month per facility vs. industry standard",
    ],
  },
  {
    key: "parking",
    name: "Parking + lots",
    accentVar: "var(--parking-accent, #6C5AA8)",
    tagline: "Monthly permits, transient billing, gate access — operator-side.",
    marketSize: "~$30B annual market · independent owners and enterprise teams alike",
    intro:
      "Run a single garage, a portfolio of surface lots, or a mixed monthly + hourly operation. We're the operator's stack — not a parker-side convenience-fee app — so the money flows through your Stripe, not a third party.",
    unitNoun: "stalls",
    capabilities: [
      {
        icon: "doc",
        title: "Permit billing + rate card",
        body: "Monthly permits with auto-renewal, multi-tier rate cards (resident vs. visitor vs. retail), and prorated start dates.",
      },
      {
        icon: "wallet",
        title: "Transient billing",
        body: "Hourly + daily charges captured at gate ingress/egress with QuickBooks-clean revenue per stall, per shift, per lot.",
      },
      {
        icon: "gate",
        title: "Gate / LPR access",
        body: "Permit-holder LPR or QR code at the gate. Visitor validation codes for retail. Operator-only override for emergencies.",
      },
      {
        icon: "chart",
        title: "Stall-level occupancy",
        body: "Live occupancy across surface + structured. Per-lot KPIs in the portfolio dashboard with a period selector.",
      },
      {
        icon: "doc",
        title: "Tax-aware billing",
        body: "Per-state parking tax tables wired in. Sales tax + city parking surcharges remitted correctly with no manual adjustment.",
      },
      {
        icon: "lock",
        title: "Towing + violation tracking",
        body: "Per-state towing rules, violation notice templates, and audit trail for every action — so disputes go your way.",
      },
    ],
    outcomes: [
      "Replace a permit spreadsheet + gate portal + accounting glue",
      "Cut convenience-fee leakage by routing payments through your Stripe",
      "Spot occupancy patterns across multiple lots at a glance",
    ],
  },
  {
    key: "rv_boat",
    name: "RV + boat storage / marinas",
    accentVar: "var(--vehicle-accent, #2D6E8F)",
    tagline: "Slips, dry stack, RV bays — vessel-aware operations.",
    marketSize: "~$10B combined marina + RV market · single-site operators are the norm",
    intro:
      "Run a marina, dry-stack yard, or RV-storage lot with the same lease engine and gate access — plus the vessel/RV asset capture you actually need. LOA-based slip pricing, USCG documentation tracking, dock plans, and dry-stack lift schedules.",
    unitNoun: "slips + bays",
    capabilities: [
      {
        icon: "grid",
        title: "Dock-plan + dry-stack editor",
        body: "Drag-and-drop dock layout per pier. Per-slip LOA brackets and rates. Dry-stack lift queue with operator confirmation.",
      },
      {
        icon: "doc",
        title: "Vessel / RV asset capture",
        body: "LOA, beam, draft, USCG documentation, hull ID, registration; or RV class (A/B/C, fifth-wheel) and length. Stays with the tenant record.",
      },
      {
        icon: "wallet",
        title: "LOA + class-based pricing",
        body: "Slip price scales with LOA brackets; RV bay price scales with class. No spreadsheet copy-paste at renewal time.",
      },
      {
        icon: "gate",
        title: "Gated dock + lot access",
        body: "Code provisions on move-in. Boater pulls up to the gate with their phone; RV operator drives in without calling the office.",
      },
      {
        icon: "doc",
        title: "Insurance + season tracking",
        body: "Hull insurance expiration tracking with auto-reminder. Seasonal vs. annual contract types and renewal cadence.",
      },
      {
        icon: "chart",
        title: "Live occupancy by slip / bay",
        body: "Real-time slip availability on the dock plan. Bay-occupancy heatmap for RV lots with pull-through bookings.",
      },
    ],
    outcomes: [
      "Replace Marina Office Manager + paper waivers + Square swipes",
      "Catch expired hull insurance before it bites you at claim time",
      "Run multi-season billing without losing a winterization charge",
    ],
  },
];

const SHARED_PLATFORM: Array<{ icon: IconName; title: string; body: string }> = [
  {
    icon: "wallet",
    title: "Stripe Connect built-in",
    body: "Tenant rent, resident rent, boater fees, parking permits — settle directly to your bank via your own Stripe. We never touch the money.",
  },
  {
    icon: "wallet",
    title: "Multi-entity accounting",
    body: "QuickBooks, Xero, Sage Intacct two-way sync per LLC. Per-Class / Location dimension mapped per entity. External-ref dedup prevents duplicate invoices.",
  },
  {
    icon: "chart",
    title: "Portfolio dashboard",
    body: "Cross-property KPIs in one view — occupancy, MTD collected, delinquent $, per-property move-in / move-out. Mobile-first.",
  },
  {
    icon: "lock",
    title: "Team permissions + audit trail",
    body: "Roles (owner / admin / manager / viewer) with per-property scopes. Tamper-evident audit trail on every state change.",
  },
];

// Per-vertical SEO overrides for /verticals/{slug}. ProductPage is
// mounted at /product, /verticals, and /verticals/{slug} via App.tsx;
// each URL needs its own canonical + title + description so Google
// doesn't fold them into duplicates of /product (SEO audit P0).
const VERTICAL_SEO: Record<string, { title: string; description: string }> = {
  "multifamily": {
    title: "Multifamily property management software — NestPlot",
    description: "Property management software for multifamily operators of every size — from a single-door landlord to a nationwide portfolio. Online rentals, tenant screening, e-sign leases, rent collection, maintenance, multi-LLC accounting — flat monthly, no per-unit surcharges.",
  },
  "self-storage": {
    title: "Self-storage software — NestPlot",
    description: "Self-storage management software for operators of every size — one facility to nationwide operators. PTI Falcon, SecurGuard, Noke, DaVinciLock, OpenTech, Janus gate vendors. Online rentals, tenant portal, state-compliant lien-sale workflow, Stripe Connect.",
  },
  "parking": {
    title: "Parking lot management software — NestPlot",
    description: "Parking management software for surface lots and structured garages of every size. Monthly permits, LPR + QR-code validation, ParkMobile import, multi-lot reporting, Stripe Connect. Flat monthly per lot, no processing margin.",
  },
  "rv-and-boat": {
    title: "Marina + RV park management software — NestPlot",
    description: "Marina, RV park, and boat-yard management software. Dock-plan editor, LOA-bracket slip pricing, RV pad capture, hull-insurance verification, gated dock access, seasonal billing cycles. Flat monthly per site.",
  },
};

export function ProductPage() {
  const params = useParams<{ vertical?: string }>();
  const slug = params.vertical;
  const verticalSeo = slug ? VERTICAL_SEO[slug] : null;
  // /product → generic. /verticals → hub landing (no slug). /verticals/{slug} → vertical-specific.
  const path = slug
    ? `/verticals/${slug}`
    : typeof window !== "undefined" && window.location.pathname === "/verticals"
      ? "/verticals"
      : "/product";
  useSeo({
    title: verticalSeo?.title ?? "Product — NestPlot property operations platform",
    description: verticalSeo?.description ?? "In-product e-signature, Stripe Connect billing, vendor gate integration, multi-entity accounting, identity verification, state-compliant lease and lien workflows — across multifamily, self-storage, parking, and RV/boat. For operators of every size, from a single landlord to the largest managers.",
    path,
    // Per-vertical SoftwareApplication schema (SEO audit P1).
    // Google exposes rating + pricing rich results for SaaS pages
    // that emit this. index.html shell already has a base
    // SoftwareApplication; here we emit a vertical-specific one
    // with the same @id (URL) so Google merges them.
    jsonLd: verticalSeo
      ? [
          {
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "@id": `https://nestplot.com/verticals/${slug}`,
            name: verticalSeo.title.split(" — ")[0],
            description: verticalSeo.description,
            applicationCategory: "BusinessApplication",
            operatingSystem: "Web",
            url: `https://nestplot.com/verticals/${slug}`,
            offers: {
              "@type": "Offer",
              price: "99",
              priceCurrency: "USD",
              priceValidUntil: "2026-12-31",
              availability: "https://schema.org/InStock",
            },
            publisher: { "@type": "Organization", name: "NestPlot" },
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://nestplot.com/" },
              { "@type": "ListItem", position: 2, name: "Verticals", item: "https://nestplot.com/verticals" },
              {
                "@type": "ListItem",
                position: 3,
                name: verticalSeo.title.split(" — ")[0],
                item: `https://nestplot.com/verticals/${slug}`,
              },
            ],
          },
        ]
      : undefined,
  });
  return (
    <div className="mx-auto max-w-[1320px] px-5 lg:px-8 py-12 lg:py-16">
      <div className="max-w-[760px]">
        <Pill tone="accent" size="sm">
          Property operations · without the enterprise sales call
        </Pill>
        <h1 className="t-display-xl mt-4">The full product</h1>
        <p className="text-sub text-lg mt-3">
          One platform across multifamily, self-storage, parking, and RV/boat —
          built for operators of every size, from a single landlord to a
          nationwide portfolio. Online rental + applications,
          gate + building access, in-product lease signing, automated billing,
          dynamic pricing, QuickBooks sync, Stripe Connect payouts. Flat monthly
          pricing. No take on your tenant rent.
        </p>
        <div className="mt-6 flex gap-3 flex-wrap">
          <Link to="/signup">
            <Btn kind="primary" size="lg" iconRight={<Icon name="arrow-right" size={16} />}>
              Try it free
            </Btn>
          </Link>
          <Link to="/pricing">
            <Btn kind="ghost" size="lg">
              See pricing
            </Btn>
          </Link>
          <Link to="/contact">
            <Btn kind="ghost" size="lg">
              Contact sales
            </Btn>
          </Link>
        </div>
        <nav className="mt-8 flex flex-wrap gap-2 text-sm">
          {VERTICAL_SECTIONS.map((v) => (
            <a
              key={v.key}
              href={`#${v.key}`}
              className="border border-line rounded-md px-3 py-1.5 font-semibold hover:bg-paper hover:border-line-strong transition-colors"
            >
              {v.name} →
            </a>
          ))}
        </nav>
      </div>

      {VERTICAL_SECTIONS.map((v, idx) => (
        <section
          key={v.key}
          id={v.key}
          className={`mt-14 ${idx > 0 ? "border-t border-line pt-14" : ""}`}
        >
          <div className="grid lg:grid-cols-[1fr_2fr] gap-8 items-start">
            <div>
              <div
                className="font-mono text-[11px] tracking-[0.18em] uppercase"
                style={{ color: v.accentVar }}
              >
                Vertical · {v.name}
              </div>
              <h2 className="t-display-l mt-2">{v.tagline}</h2>
              <div className="font-mono text-[11px] text-sub mt-3 tracking-[0.1em] uppercase">
                {v.marketSize}
              </div>
              <p className="text-sub mt-4 max-w-[420px]">{v.intro}</p>
              <div className="mt-5">
                <div className="font-mono text-[11px] text-sub tracking-[0.14em] uppercase">
                  What operators get out of it
                </div>
                <ul className="mt-3 space-y-2 text-sm">
                  {v.outcomes.map((o) => (
                    <li key={o} className="flex items-start gap-2">
                      <Icon name="check" size={14} className="text-ok mt-[3px] shrink-0" />
                      <span>{o}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {v.capabilities.map((c) => (
                <Card key={c.title}>
                  <div className="px-5 pt-3 pb-2" style={{ backgroundColor: v.accentVar }}>
                    <Slats color="var(--on-accent, #fff)" opacity={0.6} height={8} />
                  </div>
                  <div className="px-5 py-4">
                    <div className="flex items-start gap-3">
                      <div
                        className="w-9 h-9 rounded-md bg-paper border border-line grid place-items-center shrink-0"
                        style={{ color: v.accentVar }}
                      >
                        <Icon name={c.icon} size={16} />
                      </div>
                      <div>
                        <div className="font-display font-extrabold text-[15px] uppercase tracking-[-0.005em]">
                          {c.title}
                        </div>
                        <p className="text-sub text-[13px] mt-1">{c.body}</p>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>
      ))}

      <section className="mt-16 border-t border-line pt-14">
        <div className="max-w-[680px]">
          <div className="font-mono text-[11px] text-sub tracking-[0.18em] uppercase">
            One platform · every vertical
          </div>
          <h2 className="t-display-l mt-2">
            The shared platform under <span className="text-accent">all four.</span>
          </h2>
          <p className="text-sub mt-3">
            Whichever vertical you operate, you get the same accounting sync,
            billing rails, dashboard, and team permissions. Adding a second
            vertical is a new property type on the same organization — not a
            second product to learn.
          </p>
        </div>
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {SHARED_PLATFORM.map((p) => (
            <Card key={p.title}>
              <div className="bg-accent-soft px-5 pt-3 pb-2">
                <Slats color="var(--accent)" opacity={0.7} height={10} />
              </div>
              <div className="px-5 py-5">
                <div className="w-10 h-10 rounded-md bg-paper border border-line grid place-items-center text-accent">
                  <Icon name={p.icon} size={18} />
                </div>
                <div className="font-display font-extrabold text-[15px] uppercase tracking-[-0.005em] mt-3">
                  {p.title}
                </div>
                <p className="text-sub text-[13px] mt-1">{p.body}</p>
              </div>
            </Card>
          ))}
        </div>
      </section>

      <section className="mt-16 grid md:grid-cols-[1.4fr_1fr] gap-8 items-center border-t border-line pt-14">
        <div>
          <div className="font-mono text-[11px] text-sub tracking-[0.14em] uppercase">
            Compliance built in
          </div>
          <h2 className="t-display-l mt-1">
            Our commitments to <span className="text-accent">you.</span>
          </h2>
          <ul className="mt-5 grid sm:grid-cols-2 gap-3 text-sm">
            {[
              "Your data is isolated from every other operator's",
              "Your tenant and resident list is yours — export anytime",
              "Late-fee, deposit, and notice rules built in for every state",
              "Paying tenants and residents never lose access by accident",
              "Secure payments — card data never touches our servers",
              "Pricing AI is advisory — we never change your rates without your approval",
            ].map((r) => (
              <li key={r} className="flex items-start gap-2">
                <Icon name="lock" size={14} className="text-accent mt-[3px] shrink-0" />
                <span>{r}</span>
              </li>
            ))}
          </ul>
        </div>
        <Card>
          <div className="bg-ink px-5 pt-3 pb-2">
            <Slats color="var(--accent)" opacity={0.7} height={10} />
          </div>
          <div className="bg-ink px-5 py-5">
            <div className="font-mono text-[11px] text-bg opacity-70 tracking-[0.14em] uppercase">
              Our promise
            </div>
            <div className="font-display font-black text-[20px] uppercase tracking-[-0.01em] text-bg mt-2">
              Paying tenants always get in.
            </div>
            <p className="text-sm text-bg opacity-90 mt-2">
              If your gate provider is unreachable, we never silently lock out a
              paying tenant or resident. Only an explicit operator action turns
              access off.
            </p>
          </div>
        </Card>
      </section>
    </div>
  );
}
